import React, { useState, useEffect } from "react";
import { api, unwrap } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate, today as todayISO } from "@/lib/utils";
import { Check, X, Clock, Percent } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/i18n";
import { DatePicker } from "@/components/ui/calendar";
import { levelDisplayName } from "@/lib/levelLabels";

const cellFor = (status) => {
  const map = {
    present: {
      bg: "bg-[var(--success-bg)]",
      fg: "text-[var(--success)]",
      icon: Check,
    },
    absent: { bg: "bg-[var(--error-bg)]", fg: "text-[var(--error)]", icon: X },
    late: {
      bg: "bg-[var(--warning-bg)]",
      fg: "text-[var(--warning)]",
      icon: Clock,
    },
  };
  return map[status];
};

export default function AttendanceAdmin() {
  const { t, language } = useI18n();
  const [date, setDate] = useState(todayISO());
  const [sectionId, setSectionId] = useState("");
  const { data: sections } = useQuery({
    queryKey: ["sections"],
    queryFn: async () => unwrap(await api.get("/sections")),
  });
  const { data: grades } = useQuery({
    queryKey: ["grades"],
    queryFn: async () => unwrap(await api.get("/grades")),
  });

  useEffect(() => {
    if (sections && sections.length && !sectionId) setSectionId(sections[0].id);
  }, [sections, sectionId]);

  const { data: att, isLoading } = useQuery({
    queryKey: ["attendance-admin", sectionId, date],
    queryFn: async () =>
      unwrap(
        await api.get("/attendance", {
          params: { section_id: sectionId, date, session: "full_day" },
        }),
      ),
    enabled: !!sectionId,
  });

  const { data: summary } = useQuery({
    queryKey: ["attendance-summary", sectionId],
    queryFn: async () =>
      unwrap(
        await api.get("/attendance/summary", {
          params: { scope: "section", section_id: sectionId },
        }),
      ),
    enabled: !!sectionId,
  });

  const students = att?.students || [];
  const records = att?.records || [];
  // API returns students name-sorted but records in enrollment order; key by student_id, not position.
  const recordByStudent = {};
  records.forEach((r) => {
    if (r) recordByStudent[r.student_id] = r;
  });

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="attendance-admin-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
          {t("management.attendanceTitle")}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t("management.attendanceSubtitle")}
        </p>
      </div>

      <Card className="border-[var(--subtle)]">
        <CardContent className="p-4 flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] uppercase font-semibold tracking-widest text-[var(--text-muted)] mb-1">
              Section
            </div>
            <Select value={sectionId} onValueChange={setSectionId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose section" />
              </SelectTrigger>
              <SelectContent>
                {(sections || []).map((s) => {
                  const g = (grades || []).find((g) => g.id === s.grade_id);
                  return (
                    <SelectItem key={s.id} value={s.id}>
                      {levelDisplayName(g, language)} · {s.name}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] uppercase font-semibold tracking-widest text-[var(--text-muted)] mb-1">
              Date
            </div>
            <DatePicker
              value={date}
              onChange={setDate}
              max={todayISO()}
              ariaLabel="Date"
              testId="attendance-date"
            />
          </div>
        </CardContent>
      </Card>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-[var(--subtle)] bg-[var(--brand)] text-white">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase font-semibold tracking-widest opacity-70 flex items-center gap-1">
                <Percent className="h-3 w-3" /> Attendance rate
              </div>
              <div className="font-display font-bold text-3xl mt-1">
                {summary.percentage}%
              </div>
            </CardContent>
          </Card>
          {["present", "absent", "late"].map((s) => {
            const c = cellFor(s);
            return (
              <Card key={s} className="border-[var(--subtle)]">
                <CardContent className="p-4">
                  <div
                    className={`text-[10px] uppercase font-semibold tracking-widest ${c.fg} flex items-center gap-1`}
                  >
                    <c.icon className="h-3 w-3" /> {s}
                  </div>
                  <div className="font-display font-bold text-3xl mt-1">
                    {summary.counts?.[s] || 0}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Card className="border-[var(--subtle)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-display flex items-center justify-between">
            <span>Section attendance on {formatDate(date)}</span>
            {att?.locked && (
              <Badge className="bg-[var(--warning-bg)] text-[var(--warning)] border-0">
                Locked
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <Skeleton className="h-32" />
          ) : (
            <table className="w-full text-sm grid-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-2.5">#</th>
                  <th className="text-left px-4 py-2.5">Adm No.</th>
                  <th className="text-left px-4 py-2.5">Name</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => {
                  const rec = recordByStudent[s.id];
                  const c = rec ? cellFor(rec.status) : null;
                  return (
                    <tr key={s.id}>
                      <td className="px-4 py-2.5 font-mono-num text-xs">
                        {i + 1}
                      </td>
                      <td className="px-4 py-2.5 font-mono-num text-xs">
                        {s.admission_no}
                      </td>
                      <td className="px-4 py-2.5 font-medium">
                        {s.first_name} {s.last_name}
                      </td>
                      <td className="px-4 py-2.5">
                        {c ? (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${c.bg} ${c.fg} text-xs font-semibold capitalize`}
                          >
                            <c.icon className="h-3 w-3" /> {rec.status}
                          </span>
                        ) : (
                          <span className="text-xs text-[var(--text-muted)]">
                            Not marked
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {students.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-6 text-center text-[var(--text-muted)]"
                    >
                      Select a section
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
